import { useEffect, useMemo, useState } from 'react';

import { createPortal } from 'react-dom';

import cn from 'classnames';
import DOMPurify from 'dompurify';
import { useBrandingStyleVariables } from 'hooks/branding-style-variables';
import PropTypes from 'prop-types';

import { Button } from 'components/FormComponents';
import { Input } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

import styles from './Alert.module.scss';

const Alert = ({
  show = false,
  brand,
  title,
  message,
  ok,
  cancel,
  type = 'neutral',
  params = {},
  icon,
  showConfirmationInput = false,
  onOk = () => {},
  onCancel = () => {},
  okTestId,
  cancelTestId,
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const brandingStyleVariables = useBrandingStyleVariables(brand);
  const useBrandColor = Boolean(brand?.color_button);

  const getButtonType = useMemo(() => {
    switch (type) {
      case 'error':
        return 'delete';
      case 'neutral':
        return 'primary';
      case 'warning':
        return 'warn';
      default:
        return type;
    }
  }, [type]);

  const getIconType = useMemo(() => {
    switch (type) {
      case 'neutral':
        return 'primary-light';
      default:
        return type;
    }
  }, [type]);

  const isConfirmationValid =
    !showConfirmationInput || confirmationText.toLowerCase() === ok.toLowerCase();

  useEffect(() => {
    setConfirmationText('');
  }, [show]);

  return createPortal(
    <div
      data-click-outside-ignore
      className={cn(styles.root, { [styles.rootHidden]: !show }, styles[type])}>
      <div className={styles.content}>
        <div
          className={cn(
            styles.header,
            'd-flex',
            'justify-content-start',
            'align-items-start',
            'flex-column',
          )}>
          {icon && (
            <div className="u-margin-right">
              <IconComponent icon={icon} type={getIconType} border={false} size="large" />
            </div>
          )}
          <div className={styles.contentTitle}>{title}</div>
        </div>
        <div
          className={cn(styles.contentMessage)}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message ?? '') }}
        />
        {showConfirmationInput && (
          <div className={cn(styles.contentConfirmationInput, 'u-margin-y')}>
            <Input
              name="confirmation"
              placeholder={`Type '${ok}' to confirm`}
              inputProps={{
                value: confirmationText,
                onChange: e => setConfirmationText(e.target.value),
              }}
            />
          </div>
        )}
        <div className={styles.contentButtons}>
          {cancel && (
            <Button
              type="secondary"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onCancel();
              }}
              className="cancel-btn button"
              dataTestId={cancelTestId}>
              {cancel}
            </Button>
          )}
          {ok && (
            <Button
              type={getButtonType}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onOk(params);
              }}
              className={cn('button', { [styles.brandedOk]: useBrandColor })}
              style={useBrandColor ? brandingStyleVariables : undefined}
              disabled={!isConfirmationValid}
              dataTestId={okTestId}>
              {ok}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Alert;

Alert.propTypes = {
  type: PropTypes.oneOf(['neutral', 'success', 'error', 'warning', 'info']),
  title: PropTypes.string,
  message: PropTypes.string,
  ok: PropTypes.string,
  cancel: PropTypes.string,
  onOk: PropTypes.func,
  onCancel: PropTypes.func,
  brand: PropTypes.object,
};
